import { Divider, List, Grid, Box, CircularProgress, ListItem, ListItemText, LinearProgress } from "@mui/material";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { OPERATION } from '../../../helpers/enums';
import { FechaFormateada } from "../helpers";
import ArticleData from "./ArticleData";
import InfiniteScroll from "./InifiniteScroll";
import { useCurrentUser } from '../../base/UserProvider';

const Articles = React.memo(({ arts = [], optionSelected, addToCart, options, readOnly = false, asList = true, onSendMessage, fetchArticles, pageToFind = 1, loadLizy = false, externalUpdate = null }) => {
  const [page, setPage] = useState(1);
  const [articles, setArticles] = useState(arts);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const { isAdmin } = useCurrentUser();
  // Controla si ya se ha ejecutado la carga inicial
  const scrollContainerRef = useRef(null);
  const didInitialFetchRef = useRef(false);

  // Función para simular la carga de más items
  const fetchItems = async () => {
    if (loading) return;
    setLoading(true);

    const prevScrollHeight = scrollContainerRef.current?.scrollHeight;

    var resp = fetchArticles && hasMore && await fetchArticles(page)
    //fetchArticles && fetchArticles()
    if (resp)
      setArticles([...articles, ...resp]);

    // // Si no hay más artículos para cargar, deshabilitar el infinite scroll
    if (resp && resp.length < 20) {
      setHasMore(false);
    }
    hasMore && setPage(page + 1)

    setLoading(false);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight - prevScrollHeight;
    }
  }

  const handleWebUpdated = useCallback((articleId, changes = {}) => {
    setArticles((prev) => prev.map((item) => {
      if (item._id !== articleId && item.article_id !== articleId) return item;
      return {
        ...item,
        web: changes.web ?? item.web,
        article: {
          ...item.article,
          ...(changes.web !== undefined ? { web: changes.web } : {}),
          ...(changes.name ? { name: changes.name } : {}),
          ...(changes.description ? { description: changes.description } : {}),
          ...(changes.images ? { images: changes.images } : {}),
          ...(changes.type_detail ? { type_detail: changes.type_detail } : {})
        },
        prices: {
          ...item.prices,
          ...(changes.sale_price !== undefined ? { sale_price: Number(changes.sale_price) } : {})
        },
        ...(changes.type_detail ? { type_detail: changes.type_detail } : {})
      };
    }));
  }, []);

  // Aplica actualizaciones externas puntuales (ej: variantes guardadas desde Articulos.jsx)
  useEffect(() => {
    if (!externalUpdate?.articleId) return;
    handleWebUpdated(externalUpdate.articleId, externalUpdate.changes);
  }, [externalUpdate, handleWebUpdated]);

  useEffect(() => {
    if (didInitialFetchRef.current) return;
    didInitialFetchRef.current = true;
    fetchItems();
  }, [])

  const ArticlesAsList = () =>
    <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
      {articles && articles.map(article => {
        const { article: { name, description, images }, prices, quantity, article_id, created_at, to, sku, applicable_offers } = article;

        return <>
          <ArticleData
            key={article_id}
            data={{
              id: article?._id || article_id,
              name: name,
              images: images,
              description: description,
              date: FechaFormateada(created_at),
              price: to === OPERATION.VENDER ? prices?.sale_price : prices?.list_price,
              salePrice: prices?.sale_price,
              listPrice: prices?.list_price,
              quantity: quantity,
              to: to,
              sku: sku,
              applicable_offers: applicable_offers || [],
              type_detail: article.type_detail,
              newArrival: article.new_arrival,
              web: article.web,
              storeId: article.store_id,
              articleId: article.article_id,
              customerId: article?.customer?._id || article?.customer_id || ''
            }}
            optionSelected={(idOption) => optionSelected(idOption, article)}
            addToCart={(dataWithOffer) => {
              console.log('📨 Articles.jsx (LIST) received dataWithOffer:', dataWithOffer);
              // Mezclar los datos enriquecidos con oferta con el artículo original
              const enrichedArticle = {
                ...article,
                ...dataWithOffer
              };
              console.log('📨 Articles.jsx (LIST) enrichedArticle to send:', enrichedArticle);
              addToCart(enrichedArticle);
            }}
            colors={{ background: '#70AD47', font: '#FFFFFF' }}
            options={options}
            readonly={readOnly}
            imagesOnly={readOnly}
            isAdmin={isAdmin}
            onSendMessage={onSendMessage}
            onWebUpdated={handleWebUpdated}
          />
          <Divider variant="inset" component="li" />
        </>
      }
      )}
    </List>;

  const ArticlesAsGrid = () =>
    <Grid container spacing={2}>
      {articles && articles.map(article => {
        const { article: { name, description, images }, prices, quantity, article_id, created_at, to, applicable_offers } = article;

        return <Grid item xs={6} sm={1} key={article_id}>
          <ArticleData
            key={article_id}
            data={{
              id: article?._id || article_id,
              name: name,
              images: images,
              description: description,
              date: FechaFormateada(created_at),
              price: to === OPERATION.VENDER ? prices?.sale_price : prices?.list_price,
              salePrice: prices?.sale_price,
              listPrice: prices?.list_price,
              quantity: quantity,
              to: to,
              applicable_offers: applicable_offers || [],
              type_detail: article.type_detail,
              newArrival: article.new_arrival,
              web: article.web,
              storeId: article.store_id,
              articleId: article.article_id,
              customerId: article?.customer?._id || article?.customer_id || ''
            }}
            optionSelected={(idOption) => optionSelected(idOption, article)}
            addToCart={(dataWithOffer) => {
              // Mezclar los datos enriquecidos con oferta con el artículo original
              const enrichedArticle = {
                ...article,
                ...dataWithOffer
              };
              addToCart(enrichedArticle);
            }}
            colors={{ background: '#70AD47', font: '#FFFFFF' }}
            options={options}
            readonly={readOnly}
            imagesOnly={readOnly}
            isAdmin={isAdmin}
            onSendMessage={onSendMessage}
            onWebUpdated={handleWebUpdated}
          />
        </Grid>
      }
      )}
    </Grid>

  return (
    !readOnly || loadLizy ? <InfiniteScroll
      articles={articles}
      fetchItems={fetchItems}
      hasMore={hasMore}
      loading={loading}
      Child={() => <>
        {asList ? <ArticlesAsList /> : <ArticlesAsGrid />}
        <Divider variant="inset" component="li" />
      </>
      }
    /> : asList ? <ArticlesAsList /> : <ArticlesAsGrid />
  )
}, (prevProps, nextProps) => prevProps.articles !== nextProps.articles)

export default Articles;